import React from 'react';

import dayjs from 'dayjs';
import styled from 'styled-components';

import Avatar from 'common/Avatar';
import { SnapshotDataType } from 'types/snapshot';

import { useCommunityViewContext } from '../../../CommunityViewContext';

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SnapshotDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: var(--text-primary);
`;

const SnapshotName = styled.span`
  font-family: var(--font-bold);
  font-size: 13px;
  color: var(--text-primary);
`;

const SnapshotStatusText = styled.span<{ status: string }>`
  font-size: 12px;
  color: ${(props) => (props.status === 'Selected update' ? 'var(--positive-green)' : 'var(--text-secondary)')};
`;

const NoDataMessage = styled.span`
  color: var(--negative-orange);
  font-size: 12px;
`;

const NoDataIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px dashed var(--negative-orange);
  background-image: url('/icons/web-asset-off.svg');
  background-repeat: no-repeat;
  background-position: center;
`;

const ImageContainer = styled.div`
  flex-shrink: 0;
`;

interface SnapshotDetailsProps {
  snapshot?: SnapshotDataType | null;
  avatarType?: string;
  label?: string;
  noDataAvailable?: boolean;
  name?: string;
}

const SnapshotDetails = ({
  snapshot,
  avatarType = '',
  label = '',
  noDataAvailable = false,
  name,
}: SnapshotDetailsProps) => {
  const { community, competitors } = useCommunityViewContext();

  const matchedCommunity = snapshot ? [community, ...competitors].find((c) => c?.id === snapshot.community) : undefined;
  const displayName = matchedCommunity?.comunity_name || name || '';
  const date = snapshot ? dayjs(snapshot.date) : null;

  return (
    <Container>
      <ImageContainer>
        {noDataAvailable ? (
          <NoDataIcon aria-hidden />
        ) : (
          <Avatar name={displayName?.slice(0).toUpperCase()} type={avatarType} />
        )}
      </ImageContainer>
      <SnapshotDetailsContainer>
        <SnapshotName>{displayName}</SnapshotName>
        {noDataAvailable || !snapshot ? (
          <NoDataMessage>No data available</NoDataMessage>
        ) : (
          <SnapshotStatusText status={label}>
            <span>{label}</span>
            <span style={{ color: 'var(--text-secondary)', margin: '0 4px' }}>·</span>
            <span style={{ color: 'var(--text-secondary)' }}>{date?.format('MMMM YYYY')}</span>
          </SnapshotStatusText>
        )}
      </SnapshotDetailsContainer>
    </Container>
  );
};

export default SnapshotDetails;
